import { Box, Button, Flex, Image, Link, Menu, Portal, Spacer, useBreakpointValue } from '@chakra-ui/react';
import { useAuth } from '@cks-internal/react-base';
import { useMemo } from 'react';
import logo from 'src/assets/frontier-lodging-solutions-logo-v1.png';
import logosmall from 'src/assets/small-logo.svg';
import { ApplicationRoutes } from 'src/constants/applicationRoutes';
import { FrontierUser } from 'src/globalModels/frontierUser';
import { SiteModeObject } from 'src/features/misc/SiteModeObject';
import { siteModeBusiness } from 'src/constants/strings';
import { SiteModeSelector } from './SiteModeSelector';
import { NavLink } from 'react-router-dom';
import { CompanyLogoNavLoader } from './CompanyLogoNavLoader';
import { ProfileSelector } from './ProfileSelector';
import { useProfileStore } from 'src/features/stores/useProfileStore';
import { useObtProfileNav } from 'src/globalModels/api/useObtProfileNav';

export const NavBar = () => {
  const profileStore = useProfileStore();
  const selectedUser = profileStore.users?.find((u) => u.ContactId === profileStore.selectedUserId);
  const showDesktopView = useBreakpointValue({ base: false, sm: false, md: true, lg: true, xlTo2xl: true });
  const showMobileView = useBreakpointValue({ base: true, sm: true, md: false, lg: false, xlTo2xl: false });

  const auth = useAuth<FrontierUser>();
  const smo = JSON.parse(localStorage.getItem('smo')!) as SiteModeObject;
  const profileNav = useObtProfileNav();

  const userLinks = useMemo(() => {
    if (!auth.isAuthenticated) {
      return [];
    }

    const newBooking = {
      navKey: 'new-booking',
      text: 'New Booking',
      url: ApplicationRoutes.booking.hotelSearchLanding
    };
    const companyProfile = {
      navKey: 'company-profile',
      text: 'Company Profile',
      url: ApplicationRoutes.account.companyProfile
    };
    const userProfiles = {
      navKey: 'user-profiles',
      text: profileNav.userProfilesLabel,
      url: profileNav.profilePath
    };
    const myProfile = {
      navKey: 'my-profile',
      text: profileNav.myProfileLabel,
      url: profileNav.profilePath
    };
    const bookings = {
      navKey: 'booking-report',
      text: 'Booking Report',
      url: ApplicationRoutes.booking.bookingReport
    };
    const myBookings = {
      navKey: 'my-booking-report',
      text: 'My Booking Report',
      url: ApplicationRoutes.booking.bookingReport
    };
    const manageBookings = {
      navKey: 'manage-bookings',
      text: 'Manage Bookings',
      url: ApplicationRoutes.booking.manageBookings
    };

    const businessModeSuperUserItems = [newBooking, manageBookings, companyProfile, userProfiles, bookings];
    const businessModeNonSuperUserItems = [newBooking, manageBookings, myProfile, bookings];
    const personalModeSuperUserItems = [newBooking, manageBookings, myProfile, myBookings];
    const personalModeNonSuperUserItems = [newBooking, manageBookings, myProfile, myBookings];

    if (smo?.currentMode == siteModeBusiness) {
      return selectedUser?.ObtSuperUser ? businessModeSuperUserItems : businessModeNonSuperUserItems;
    }
    return selectedUser?.ObtSuperUser ? personalModeSuperUserItems : personalModeNonSuperUserItems;
  }, [auth.isAuthenticated, selectedUser, smo?.currentMode, profileNav]);

  return (
    <>
      {/* Standard nav */}
      {showDesktopView && (
        <Box position={'fixed'} width={'100%'} zIndex={'sticky'}>
          <Box
            bg={'white'}
            height={'70px'}
            marginBottom={'0px'}
            borderTopWidth={'10px'}
            borderTopColor={smo && smo.currentMode == siteModeBusiness ? 'primary.500' : 'secondary.500'}
            borderStyle={'solid'}
            boxSizing='content-box'
            boxShadow={'none'}
            borderBottom={'1px solid rgb(221, 222, 224)'}>
            <Flex
              w={'100%'}
              h={'100%'}
              gap={'24px'}
              paddingEnd={'24px'}
              alignItems={'center'}
              justifyContent={'space-between'}>
              <Link href='/' paddingLeft={'20px'} paddingRight={'20px'}>
                <Image height={['40px']} src={logo} alt='Frontier Lodging Solutions' />
              </Link>
              {/* <ChakraBreakpoints isLocalHost={isLocalhost} /> */}
              {showDesktopView && auth.isAuthenticated && <SiteModeSelector />}
              <CompanyLogoNavLoader />
              {auth.isAuthenticated && (
                <>
                  <Spacer />
                  {userLinks.map((link: { navKey: string; text: string; url: string }) => (
                    <Link asChild key={link.navKey} variant='plain'>
                      <NavLink to={link.url}>
                        <strong>{link.text}</strong>
                      </NavLink>
                    </Link>
                  ))}
                  <Spacer />
                  <ProfileSelector />
                </>
              )}
            </Flex>
          </Box>
        </Box>
      )}

      {/* Mobile nav */}
      {showMobileView && (
        <Box position={'fixed'} width={'100%'} zIndex={'sticky'}>
          <Box
            bg={'white'}
            height={'50px'}
            marginBottom={'0px'}
            borderTopWidth={'5px'}
            borderTopColor={smo && smo.currentMode == siteModeBusiness ? 'primary.500' : 'secondary.500'}
            borderStyle={'solid'}
            boxSizing='content-box'
            boxShadow={'none'}
            borderBottom={'1px solid rgb(221, 222, 224)'}>
            <Flex w={'100%'} h={'100%'} alignItems={'center'}>
              <Box paddingLeft={'8px'}>
                <Link href='/'>
                  <Image height={['40px']} src={logosmall} alt='Frontier Lodging Solutions' />
                </Link>
              </Box>
              {/* <ChakraBreakpoints isLocalHost={isLocalhost} /> */}
              {auth.isAuthenticated && (
                <Box marginLeft={'8px'}>
                  <SiteModeSelector />
                </Box>
              )}
              <Box marginLeft={'8px'} marginRight={'8px'}>
                <CompanyLogoNavLoader />
              </Box>
              {auth.isAuthenticated && (
                <>
                  <Spacer />
                  <Menu.Root>
                    <Menu.Trigger asChild>
                      <Button
                        padding={'0px 8px 0px 8px'}
                        marginRight={'8px'}
                        variant={'outline'}
                        border={'1px solid rgb(221, 222, 224)'}
                        fontSize={'sm'}
                        fontWeight={'semibold'}>
                        Menu
                      </Button>
                    </Menu.Trigger>
                    <Portal>
                      <Menu.Positioner>
                        <Menu.Content>
                          <Menu.ItemGroup>
                            {userLinks.map((link: { navKey: string; text: string; url: string }) => (
                              <Menu.Item key={link.navKey} asChild value={link.text}>
                                <a href={link.url} target='_blank' rel='noreferrer' style={{ fontWeight: 'bold' }}>
                                  {link.text}
                                </a>
                              </Menu.Item>
                            ))}
                          </Menu.ItemGroup>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Portal>
                  </Menu.Root>
                  <Box marginRight={'8px'}>
                    <ProfileSelector />
                  </Box>
                </>
              )}
            </Flex>
          </Box>
        </Box>
      )}
    </>
  );
};
